import "react-app-polyfill/stable";
import "react-app-polyfill/ie11";
import RotateNotice from "./components/RotateNotice";
import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom";
import { Route, Switch, BrowserRouter, HashRouter, useHistory } from "react-router-dom";
import { initReminders } from "./dailyReminder";
import { isEmbedded, initPortal, portalStartupReady } from "./portal";
import * as serviceWorker from "./serviceWorker";
import { Provider } from "react-redux";
import store from "./redux/store";
import { firebaseReady, onFirebaseInit } from "./fb";
import { DndProvider } from "react-dnd";
import { TouchBackend } from "react-dnd-touch-backend";

// Initialize Firestore
import { initializeFirebase, setAuthPersistence } from "./components/Firestore";
import FullPageLoading from "./components/FullPageLoading";
import { initializeAudio, initializeErrorReporting } from "./utils";
import { initNativeAds } from "./nativeAds";
import { initPlayGames } from "./playGames";
import { installNativeLinkHandler } from "./appLinks";
import { loadAdSense } from "./adsense";
import { isLocalMode } from "./localStore";
import { Helmet } from "react-helmet";
import { getLang, initLang, isRtl, onLangChange, t } from "./i18n";
// Local mode (Playgama) runs with no server at all: no Firebase, no analytics.
initializeFirebase();
initializeAudio();
initializeErrorReporting();
initNativeAds(); // AdMob, apps only: APP_ADS_ENABLED (ads.ts)
installNativeLinkHandler(); // apps only: links to website pages, mail and other sites open outside the app
initPlayGames(); // achievements: Play Games (Android) / Game Center (iPhone), apps only
loadAdSense(); // AdSense, website only: WEBSITE_ADSENSE_ENABLED (ads.ts)

if (!isLocalMode) onFirebaseInit(() => setAuthPersistence("none")); // firebase.auth.Auth.Persistence.NONE

// Firebase isn't part of the first download (see fb.ts): the start screen and the policy pages
// show without it, and every screen that uses it waits for it (and for its own code) first.
// Playgama, GamePix and Y8 have no Firebase to wait for, and keeps the game table and create screen in the main
// bundle as before (webpack drops whichever branch isn't taken).
const withFirebase = <T,>(load: () => Promise<T>) => () => Promise.all([firebaseReady(), load()]).then(([, m]) => m);
const loadGameView = () => import(/* webpackChunkName: "GameView" */ "./GameView");
const loadCreateGame = () => import(/* webpackChunkName: "CreateGame" */ "./CreateGame");
/* eslint-disable global-require */
const GameView: any = (process.env.REACT_APP_PORTAL === "playgama" || process.env.REACT_APP_PORTAL === "gamepix" || process.env.REACT_APP_PORTAL === "y8") ? require("./GameView").default : lazy(withFirebase(loadGameView));
const CreateGame: any = (process.env.REACT_APP_PORTAL === "playgama" || process.env.REACT_APP_PORTAL === "gamepix" || process.env.REACT_APP_PORTAL === "y8") ? require("./CreateGame").default : lazy(withFirebase(loadCreateGame));
/* eslint-enable global-require */
// Once Firebase is here, fetch the game table's code too, so Quick Play doesn't wait for it.
if (!(process.env.REACT_APP_PORTAL === "playgama" || process.env.REACT_APP_PORTAL === "gamepix" || process.env.REACT_APP_PORTAL === "y8")) onFirebaseInit(() => setTimeout(() => loadGameView().catch(() => undefined), 0));
const ResetPasswordLink = lazy(withFirebase(() => import(/* webpackChunkName: "ResetPasswordLink" */ "./ResetPasswordLink")));
const ChangePassword = lazy(withFirebase(() => import(/* webpackChunkName: "ChangePassword" */ "./ChangePassword")));

const Landing = lazy(() => import(/* webpackChunkName: "Landing" */ "./Landing"));
const QuickPlay = lazy(withFirebase(() => import(/* webpackChunkName: "QuickPlay" */ "./QuickPlay")));
const Daily = lazy(withFirebase(() => import(/* webpackChunkName: "Daily" */ "./Daily")));
const Challenge = lazy(withFirebase(() => import(/* webpackChunkName: "Challenge" */ "./Challenge")));
const Login = lazy(withFirebase(() => import(/* webpackChunkName: "Login" */ "./Login")));
const Signup = lazy(withFirebase(() => import(/* webpackChunkName: "Signup" */ "./Signup")));
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "PrivacyPolicy" */ "./components/PrivacyPolicy"));
const CookiePolicy = lazy(() => import(/* webpackChunkName: "CookiePolicy" */ "./components/CookiePolicy"));
const Tutorial = lazy(() => import(/* webpackChunkName: "Tutorial" */ "./Tutorial"));
const TermsOfUse = lazy(() => import(/* webpackChunkName: "TermsOfUse" */ "./components/TermsOfUse"));
const ShortPrivacyPolicy = lazy(
  () => import(/* webpackChunkName: "ShortPrivacyPolicy" */ "./components/ShortPrivacyPolicy")
);
const Attribution = lazy(() => import(/* webpackChunkName: "Attribution" */ "./components/Attribution"));
const PolicyList = lazy(() => import(/* webpackChunkName: "PolicyList" */ "./components/PolicyList"));
const NotFound = lazy(() => import(/* webpackChunkName: "NotFound" */ "./components/NotFound"));
const Admin = lazy(withFirebase(() => import(/* webpackChunkName: "Admin" */ "./Admin")));
const EditAd = lazy(withFirebase(() => import(/* webpackChunkName: "EditAd" */ "./EditAd")));
const ListAds = lazy(withFirebase(() => import(/* webpackChunkName: "ListAds" */ "./ListAds")));
const ChangeUsername = lazy(withFirebase(() => import(/* webpackChunkName: "ChangeUsername" */ "./ChangeUsername")));

// Portal builds run inside another site, so routes live after "#" in the address.
const Router: any = isEmbedded ? HashRouter : BrowserRouter;

// The apps' optional daily reminder: refresh the week ahead on launch, and open /daily from a tapped
// reminder. Does nothing outside the iPhone/Android app (see dailyReminder.ts).
const ReminderBoot: React.FC = () => {
  const history = useHistory();
  React.useEffect(() => {
    initReminders((route) => history.push(route));
  }, [history]);
  return null;
};

// Re-renders everything (without a reload) when the language changes: the routes under it
// mount again with the new strings. The game's state lives in the store and is kept.
const LangRoot: React.FC = ({ children }) => {
  const [lang, setLang] = React.useState(getLang());
  React.useEffect(() => onLangChange(setLang), []);
  return (
    <React.Fragment key={lang}>
      <Helmet>
        <meta name="description" content={t("site.description")} />
      </Helmet>
      {/* Korean wraps at spaces (not inside words); Japanese keeps small kana and 。 off a line start. */}
      {lang === "ko" && <style>{"body{word-break:keep-all;overflow-wrap:anywhere}"}</style>}
      {lang === "ja" && <style>{"body{line-break:strict;overflow-wrap:anywhere}"}</style>}
      {/* Chinese: no spaces between words, so lines break anywhere, but not before 。，！ */}
      {lang === "zh-CN" && <style>{"body{line-break:strict;overflow-wrap:anywhere}"}</style>}
      {/* German and Polish have long compound words: let them hyphenate rather than overflow a button. */}
      {(lang === "de" || lang === "pl") && <style>{"body{overflow-wrap:break-word}button{hyphens:auto}"}</style>}
      {/* Right to left: the rules panel waits off-screen to the right, which a right-to-left page
          would otherwise count as width (phones then zoom out). Clip it at the page edge. */}
      {isRtl(lang) && <style>{"html{overflow-x:hidden}"}</style>}
      {children}
      <RotateNotice />
    </React.Fragment>
  );
};

const routing = (
  <React.StrictMode>
    <DndProvider
      backend={TouchBackend}
      options={{
        enableMouseEvents: true,
      }}
    >
      <Provider store={store}>
        <Router>
          <ReminderBoot />
          <LangRoot>
          <Suspense fallback={<FullPageLoading />}>
            <div>
              <Switch>
              {/* Add these routes to firebase.json so that direct navigation works*/}
              <Route exact path="/" component={Landing} />
              <Route path="/login" component={Login} />
              <Route path="/game/:roomCode" component={GameView} />
              <Route path="/create" component={CreateGame} />
              <Route path="/play" component={QuickPlay} />
              <Route path="/daily" component={Daily} />
              <Route path="/challenge/:seed" component={Challenge} />
              <Route path="/signup" component={Signup} />
              <Route path="/admin/list-ads" component={ListAds} />
              <Route path="/admin/create-ad" component={EditAd} />
              <Route path="/admin" component={Admin} />
              <Route path="/forgot-password" component={ResetPasswordLink} />
              <Route path="/change-username" component={ChangeUsername} />
              <Route path="/change-password/:uid/:token" component={ChangePassword} />
              <Route path="/cookie-policy" component={CookiePolicy} />
              <Route path="/privacy-notice" component={ShortPrivacyPolicy} />
              <Route path="/privacy-policy" component={ShortPrivacyPolicy} />
              <Route path="/terms" component={TermsOfUse} />
              <Route path="/learn" component={Tutorial} />
              <Route path="/attribution" component={Attribution} />
              <Route path="/policies" component={PolicyList} />
              <Route component={NotFound} />
              </Switch>
            </div>
          </Suspense>
          </LangRoot>
        </Router>
      </Provider>
    </DndProvider>
  </React.StrictMode>
);

initPortal();
// Japanese and Korean strings come in their own small file: load it first (English needs nothing).
Promise.all([initLang(), portalStartupReady()]).then(() => ReactDOM.render(routing, document.getElementById("root")));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
// Website only: a minimal service worker (no caching) so the site can be installed as an app.
// Portals and the phone app don't use one.
if (!isEmbedded && !(window as any).Capacitor?.isNativePlatform?.() && "serviceWorker" in navigator && window.location.protocol === "https:") {
  navigator.serviceWorker.register("/sw.js").catch(() => undefined);
} else {
  serviceWorker.unregister();
}
